import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../lib/api/client';
import type { CampoEditavel, Checkpoint, OpcoesDoEditor } from '../lib/api/types';
import { useAuth } from '../lib/auth/AuthContext';
import { PageHeader } from '../components/ui/PageHeader';
import { Carregando, Erro } from '../components/ui/Estados';

/* --------------------------------------------------------------- checkpoints (A11) */

/**
 * O editor de checkpoint — **a tela que gera o formulário do aplicativo**.
 *
 * ## O que ela decide, e por que é a mais perigosa do painel
 * O que se salva aqui é o que o operador vê na próxima sessão que abrir, no galpão, sem
 * distribuição de aplicativo. É o que torna o produto configurável pelo cliente — e é o que
 * permite, com um clique errado, parar uma doca inteira.
 *
 * Três defesas, e nenhuma delas é um aviso na tela:
 *
 * 1. **O servidor confere antes de gravar** (`CampoDoCheckpoint`). Tipo inventado, chave
 *    inválida, seleção sem opções: recusados com a razão em português.
 * 2. **Os tipos e os passos vêm do servidor**, então esta tela não consegue oferecer o que ele
 *    recusa (ADR-097).
 * 3. **A prévia mostra o turno**, e é a defesa que importa: campo por item interrompe a
 *    leitura em série a cada peça (ADR-049), e essa é a diferença que muda o ritmo do turno
 *    inteiro. Quem configura precisa ver isso enquanto configura, não descobrir pelo telefone.
 */
export function CheckpointsPage() {
  const { traduzir } = useAuth();
  const [editando, setEditando] = useState<Checkpoint | 'novo' | null>(null);

  const checkpoints = useQuery({
    queryKey: ['checkpoints'],
    queryFn: () => api.get<Checkpoint[]>('/console/checkpoints'),
  });

  return (
    <>
      <PageHeader
        titulo="Checkpoints"
        subtitulo="Os pontos da cadeia onde se lê. É esta configuração que vira o formulário do aplicativo."
      />
      <div className="wrap">
        {editando ? (
          <Editor
            inicial={editando === 'novo' ? null : editando}
            aoSair={() => setEditando(null)}
          />
        ) : (
          <>
            <button
              className="btn btn-primary"
              style={{ marginBottom: 16 }}
              onClick={() => setEditando('novo')}
            >
              Novo checkpoint
            </button>

            {checkpoints.isPending && <Carregando o="os checkpoints" />}
            {checkpoints.isError && <Erro erro={checkpoints.error} />}
            {checkpoints.data && (
              <div className="card tbl">
                <table className="t">
                  <thead>
                    <tr>
                      <th>Checkpoint</th>
                      <th>Passo da cadeia</th>
                      <th>O que pede</th>
                      <th>Origem</th>
                      <th>Fecha sozinho em</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {checkpoints.data.map((c) => (
                      <tr key={c.id}>
                        <td style={{ fontWeight: 600 }}>
                          {c.name}
                          {!c.active && (
                            <span className="chip chip-neutral" style={{ marginLeft: 8 }}>
                              inativo
                            </span>
                          )}
                        </td>
                        <td>{traduzir('bizStep', c.bizStep)}</td>
                        <td>{resumoDosCampos(c.requiredFields)}</td>
                        <td>
                          <span className={c.proprio ? 'chip chip-teal' : 'chip chip-neutral'}>
                            {c.proprio ? 'sua empresa' : 'modelo global'}
                          </span>
                        </td>
                        <td className="num">{c.autoCloseMinutos} min</td>
                        <td>
                          {/* Modelo global não se edita: mudaria o processo das outras
                              empresas. O servidor recusa, e a tela nem oferece. */}
                          {c.proprio && (
                            <button className="btn btn-ghost" onClick={() => setEditando(c)}>
                              Editar
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

/** Uma frase por checkpoint na lista, para não ter que abrir cada um para saber o que pede. */
function resumoDosCampos(bruto: string): string {
  const campos = lerCampos(bruto);
  if (campos.length === 0) return 'nada';
  const naAbertura = campos.filter((c) => c.scope === 'SESSION').length;
  const porItem = campos.filter((c) => c.scope === 'ITEM').length;
  const partes: string[] = [];
  if (naAbertura > 0) partes.push(`${naAbertura} na abertura`);
  if (porItem > 0) partes.push(`${porItem} por item`);
  return partes.join(' · ');
}

/**
 * Lê o JSON gravado. **Nunca estoura**: um `required_fields` que esta versão do painel não
 * entende não pode derrubar a listagem inteira — a mesma razão pela qual o aplicativo trata
 * tipo desconhecido como não suportado em vez de recusar o checkpoint.
 */
function lerCampos(bruto: string | null | undefined): CampoEditavel[] {
  if (!bruto) return [];
  try {
    const lido = JSON.parse(bruto);
    return Array.isArray(lido) ? (lido as CampoEditavel[]) : [];
  } catch {
    return [];
  }
}

/* ------------------------------------------------------------------------- o editor */

function Editor({ inicial, aoSair }: { inicial: Checkpoint | null; aoSair: () => void }) {
  const clienteDeConsulta = useQueryClient();
  const { traduzir } = useAuth();

  const [code, setCode] = useState(inicial?.code ?? '');
  const [name, setName] = useState(inicial?.name ?? '');
  const [bizStep, setBizStep] = useState(inicial?.bizStep ?? '');
  const [autoClose, setAutoClose] = useState(inicial?.autoCloseMinutos ?? 720);
  const [ativo, setAtivo] = useState(inicial?.active ?? true);
  const [campos, setCampos] = useState<CampoEditavel[]>(lerCampos(inicial?.requiredFields));

  const opcoes = useQuery({
    queryKey: ['opcoes-do-editor'],
    queryFn: () => api.get<OpcoesDoEditor>('/console/checkpoints/opcoes'),
  });

  const salvar = useMutation({
    mutationFn: () => {
      const corpo = {
        code,
        name,
        bizStep,
        disposition: inicial?.disposition ?? null,
        requiredFields: JSON.stringify(campos),
        autoCloseMinutes: autoClose,
        active: ativo,
      };
      return inicial
        ? api.put<Checkpoint>(`/console/checkpoints/${inicial.id}`, corpo)
        : api.post<Checkpoint>('/console/checkpoints', corpo);
    },
    onSuccess: () => {
      void clienteDeConsulta.invalidateQueries({ queryKey: ['checkpoints'] });
      aoSair();
    },
  });

  function trocar(i: number, mudanca: Partial<CampoEditavel>) {
    setCampos(campos.map((c, j) => (i === j ? { ...c, ...mudanca } : c)));
  }

  return (
    <div className="duas">
      <div className="card cd">
        <div className="cd2-t">{inicial ? `Editar ${inicial.name}` : 'Novo checkpoint'}</div>

        <div className="form">
          <div className="campo">
            <label htmlFor="cod">Código</label>
            <input id="cod" value={code} onChange={(e) => setCode(e.target.value)} required />
            <span className="dica">Identificador curto, para relatório e integração.</span>
          </div>
          <div className="campo">
            <label htmlFor="nom">Nome</label>
            <input id="nom" value={name} onChange={(e) => setName(e.target.value)} required />
            <span className="dica">É este nome que o operador vê na lista do aplicativo.</span>
          </div>
          <div className="campo">
            <label htmlFor="pas">Passo da cadeia</label>
            <select id="pas" value={bizStep} onChange={(e) => setBizStep(e.target.value)} required>
              <option value="">Escolha o passo</option>
              {opcoes.data?.passos.map((p) => (
                <option key={p} value={p}>
                  {traduzir('bizStep', p)}
                </option>
              ))}
            </select>
            <span className="dica">
              É o que o evento vai registrar. Não muda depois que houver leitura.
            </span>
          </div>
          <div className="campo">
            <label htmlFor="auto">Fecha sozinho em (minutos)</label>
            <input
              id="auto"
              type="number"
              min={5}
              max={1440}
              value={autoClose}
              onChange={(e) => setAutoClose(Number(e.target.value))}
            />
            <span className="dica">
              Sem isto, um turno esquecido aberto trava o aparelho para a próxima sessão.
            </span>
          </div>
          <div className="campo">
            <label>
              <input type="checkbox" checked={ativo} onChange={(e) => setAtivo(e.target.checked)} />{' '}
              Ativo
            </label>
            <span className="dica">
              Desativar tira da lista do aplicativo. O histórico continua apontando para ele —
              por isso não há apagar.
            </span>
          </div>

          {salvar.isError && <div className="aviso ruim">{String(salvar.error)}</div>}

          <div style={{ display: 'flex', gap: 10 }}>
            <button
              className="btn btn-primary"
              disabled={salvar.isPending}
              onClick={() => salvar.mutate()}
            >
              {inicial ? 'Salvar' : 'Criar checkpoint'}
            </button>
            <button className="btn" onClick={aoSair}>
              Cancelar
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="cd2-t">Campos do formulário</div>
        <p className="dica">
          O que o operador responde. <b>Na abertura</b> é perguntado uma vez por turno;{' '}
          <b>por item</b> interrompe a leitura a cada peça — e é por isso que ele deve ser
          exceção.
        </p>

        {opcoes.isError && <Erro erro={opcoes.error} />}

        {campos.map((c, i) => (
          <div key={i} className="card" style={{ marginTop: 12, padding: 12 }}>
            <div style={{ display: 'grid', gap: 8, gridTemplateColumns: '1fr 1fr' }}>
              <div className="campo">
                <label>Chave</label>
                <input
                  value={c.key}
                  onChange={(e) => trocar(i, { key: e.target.value })}
                  placeholder="nfe_saida"
                />
              </div>
              <div className="campo">
                <label>Rótulo</label>
                <input
                  value={c.label ?? ''}
                  onChange={(e) => trocar(i, { label: e.target.value })}
                  placeholder="Chave da NF-e"
                />
              </div>
              <div className="campo">
                <label>Tipo</label>
                <select value={c.type} onChange={(e) => trocar(i, { type: e.target.value })}>
                  {opcoes.data?.tipos.map((t) => (
                    <option key={t.tipo} value={t.tipo}>
                      {t.rotulo}
                    </option>
                  ))}
                </select>
              </div>
              <div className="campo">
                <label>Quando</label>
                <select
                  value={c.scope}
                  onChange={(e) => trocar(i, { scope: e.target.value as 'SESSION' | 'ITEM' })}
                >
                  <option value="SESSION">Na abertura</option>
                  <option value="ITEM">A cada item</option>
                </select>
              </div>
            </div>

            {/* Só o que o tipo exige aparece: um campo de opções vazio ao lado de um texto
                é um convite a preencher o que não vai a lugar nenhum. */}
            {c.type === 'select' && (
              <div className="campo">
                <label>Opções, separadas por vírgula</label>
                <input
                  value={(c.options ?? []).join(', ')}
                  onChange={(e) =>
                    trocar(i, {
                      options: e.target.value
                        .split(',')
                        .map((o) => o.trim())
                        .filter((o) => o.length > 0),
                    })
                  }
                />
              </div>
            )}
            {c.type === 'nfe' && (
              <div className="campo">
                <label>Modelo</label>
                <select
                  value={c.modelo ?? '55'}
                  onChange={(e) => trocar(i, { modelo: e.target.value })}
                >
                  <option value="55">55 — DANFE (código de barras)</option>
                  <option value="65">65 — NFC-e (QR)</option>
                </select>
                <span className="dica">Decide qual leitor o aplicativo abre (ADR-050).</span>
              </div>
            )}

            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 8 }}>
              <label>
                <input
                  type="checkbox"
                  checked={c.required ?? false}
                  onChange={(e) => trocar(i, { required: e.target.checked })}
                />{' '}
                Obrigatório
              </label>
              <button
                className="btn btn-ghost"
                onClick={() => setCampos(campos.filter((_, j) => j !== i))}
              >
                Remover
              </button>
            </div>
          </div>
        ))}

        <button
          className="btn"
          style={{ marginTop: 12 }}
          onClick={() =>
            setCampos([
              ...campos,
              { key: '', label: '', type: 'text', scope: 'SESSION', required: false },
            ])
          }
        >
          Acrescentar campo
        </button>

        <Previa campos={campos} />
      </div>
    </div>
  );
}

/**
 * O turno resultante, em duas frases.
 *
 * É a parte da tela que evita o erro que custa caro: um campo por item transforma uma leitura
 * em série de quinhentas peças em quinhentas interrupções. Quem configura vê isso aqui,
 * enquanto configura — e não pelo telefone, no dia seguinte, com o caminhão parado.
 */
function Previa({ campos }: { campos: CampoEditavel[] }) {
  const naAbertura = campos.filter((c) => c.scope === 'SESSION');
  const porItem = campos.filter((c) => c.scope === 'ITEM');

  return (
    <div className="aviso" style={{ marginTop: 16 }}>
      <b>Como fica o turno</b>
      <div style={{ marginTop: 6 }}>
        {naAbertura.length === 0
          ? 'Na abertura, o operador não responde nada.'
          : `Na abertura, o operador responde: ${naAbertura
              .map((c) => c.label || c.key || '(sem nome)')
              .join(', ')}.`}
      </div>
      <div style={{ marginTop: 4 }}>
        {porItem.length === 0 ? (
          'A leitura corre sem interrupção.'
        ) : (
          <>
            {/* "a série para para" — o verbo e a preposição colados. Apareceu na tela. */}
            <b>A cada peça</b> a leitura é interrompida para:{' '}
            {porItem.map((c) => c.label || c.key || '(sem nome)').join(', ')}.
          </>
        )}
      </div>
    </div>
  );
}
